import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Loader2,
  Users,
  CalendarDays,
  TrendingUp,
  ChevronRight,
  Filter,
  ArrowLeft,
  Receipt,
  Download,
} from "lucide-react";
import SocioPaymentsCard from "@/components/SocioPaymentsCard";
import SocioMonthlyRepasse from "@/components/SocioMonthlyRepasse";
import DateFieldPT from "@/components/DateFieldPT";
import MonthFieldPT from "@/components/MonthFieldPT";

const downloadSocioCsv = (socio, items, periodLabel) => {
  if (!items?.length) return;
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = ["Nº Orçamento", "Data NF", "NF", "Escola", "Bruto", "Líquido", "Reserva", "%", "Faixa", "Valor sócio"];
  const rows = items.map((r) => [
    r.budget_number, r.nf_date, r.nf_number, r.school_name,
    r.gross, r.net_after_taxes_expenses, r.reserve_value, r.pct, `F${r.tier}`, r.value,
  ].map(esc).join(";"));
  const csv = [header.map(esc).join(";"), ...rows].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `socio-${(socio?.name || "relatorio").toLowerCase().replace(/\s+/g, "-")}-${periodLabel}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

/* -----------------------------------------------------------------
 * SociosList — admin landing page for /financeiro/socios
 *   Shows the 3 socios with a quick "total this month" preview and
 *   links to each individual report.
 * ----------------------------------------------------------------- */
export function SociosList() {
  const [socios, setSocios] = useState([]);
  const [previews, setPreviews] = useState({});
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const nowMonth = new Date().toISOString().slice(0, 7);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/finance/socios");
        const list = r.data || [];
        setSocios(list);
        // preload "this month" totals + overall balances in parallel
        const [results, balancesRes] = await Promise.all([
          Promise.all(
            list.map((s) =>
              api
                .get(`/finance/socios/${s.id}/report`, { params: { month: nowMonth } })
                .then((x) => [s.id, x.data?.total || 0])
                .catch(() => [s.id, 0])
            )
          ),
          api.get("/finance/socios-balances").then((x) => x.data || []).catch(() => []),
        ]);
        setPreviews(Object.fromEntries(results));
        const balMap = {};
        for (const b of balancesRes) balMap[b.socio?.id] = b;
        setBalances(balMap);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading)
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando…
      </div>
    );

  return (
    <div className="space-y-5 max-w-5xl" data-testid="finance-socios-list">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center">
          <Users className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-black text-slate-900">
            Relatórios dos sócios
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Demonstrativos individuais com filtros por período, escola e orçamento.
          </p>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {socios.map((s, idx) => (
          <Link
            key={s.id}
            to={`/financeiro/socios/${s.id}`}
            data-testid={`socio-card-${s.id}`}
            className="group relative overflow-hidden rounded-2xl bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-xl hover:-translate-y-0.5 transition-all p-5"
          >
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-emerald-100/50 blur-2xl group-hover:bg-emerald-200/70" />
            <div className="relative">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-black">
                  {idx + 1}
                </div>
                <p className="text-[10px] uppercase tracking-widest font-black text-emerald-700">
                  Sócio
                </p>
              </div>
              <h2 className="mt-3 font-heading text-lg font-black text-slate-900 truncate">
                {s.name}
              </h2>
              <p className="text-[10px] text-slate-500 mt-0.5">
                F1 {s.pct_t1}% • F2 {s.pct_t2}% • F3 {s.pct_t3}%
              </p>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-end justify-between">
                <div>
                  <p className="text-[9px] uppercase tracking-widest text-slate-400 font-black">
                    Este mês
                  </p>
                  <p className="text-lg font-black tabular-nums text-slate-900">
                    {BRL(previews[s.id] || 0)}
                  </p>
                  {balances[s.id] ? (
                    <p
                      className={`text-[10px] font-bold mt-1 ${
                        (balances[s.id]?.balance || 0) <= 0.009
                          ? "text-emerald-600"
                          : "text-amber-700"
                      }`}
                      data-testid={`socio-balance-${s.id}`}
                    >
                      {(balances[s.id]?.balance || 0) <= 0.009
                        ? "✓ Pagamentos em dia"
                        : `Saldo pendente: ${BRL(balances[s.id]?.balance || 0)}`}
                    </p>
                  ) : null}
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-700 group-hover:translate-x-0.5 transition-all" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------
 * SocioReport — /financeiro/socios/:socioId
 * ----------------------------------------------------------------- */
export function SocioReport() {
  const { socioId } = useParams();
  const [filters, setFilters] = useState({
    month: new Date().toISOString().slice(0, 7),
    date_from: "",
    date_to: "",
    school_id: "",
    budget_number: "",
  });
  const [mode, setMode] = useState("month"); // month | range
  const [schools, setSchools] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/schools?limit=500");
        setSchools(r.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (mode === "month" && filters.month) params.month = filters.month;
      if (mode === "range") {
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
      }
      if (filters.school_id) params.school_id = filters.school_id;
      if (filters.budget_number) params.budget_number = filters.budget_number;
      const r = await api.get(`/finance/socios/${socioId}/report`, { params });
      setData(r.data || null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [filters, mode, socioId]);

  const socio = data?.socio;
  const total = data?.total || 0;
  const items = data?.items || [];
  const byMonth = data?.by_month || [];

  const periodLabel = useMemo(() => {
    if (mode === "month") return filters.month || "—";
    if (filters.date_from || filters.date_to) {
      return `${filters.date_from || "…"} → ${filters.date_to || "…"}`;
    }
    return "Todo o período";
  }, [mode, filters]);

  return (
    <div className="space-y-5 max-w-5xl pb-16" data-testid="finance-socio-report-page">
      <div className="flex items-center gap-3">
        <Link to="/financeiro/socios" className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center">
          <ArrowLeft className="w-4 h-4 text-slate-700" />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-black text-slate-900 truncate">
            {socio?.name || "Sócio"}
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Demonstrativo individual • {periodLabel}
          </p>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-4" data-testid="socio-total">
          <p className="text-[10px] font-black uppercase tracking-widest opacity-80">Total no período</p>
          <p className="mt-2 text-xl sm:text-2xl font-black tabular-nums">{BRL(total)}</p>
          <p className="text-[10px] opacity-80 mt-0.5">{items.length} orçamento(s)</p>
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
            Média por orçamento
          </p>
          <p className="mt-2 text-xl sm:text-2xl font-black tabular-nums text-slate-900">
            {BRL(items.length ? total / items.length : 0)}
          </p>
        </div>
      </div>

      {/* Payments / balance panel */}
      {socio && <SocioPaymentsCard socio={socio} />}

      {/* Monthly repasse report (envio ao sócio) */}
      {socio && <SocioMonthlyRepasse socio={socio} />}

      {/* Filters */}
      <section className="bg-white rounded-2xl border border-slate-200 p-4 card-shadow">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-700" />
          <h2 className="font-heading text-sm font-black text-slate-900">Filtros</h2>
        </div>
        <div className="flex flex-wrap gap-2 mb-3 text-[12px]">
          <button
            onClick={() => setMode("month")}
            className={`px-3 h-8 rounded-lg font-bold ${mode === "month" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}
          >
            Por mês
          </button>
          <button
            onClick={() => setMode("range")}
            className={`px-3 h-8 rounded-lg font-bold ${mode === "range" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}
          >
            Período personalizado
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          {mode === "month" ? (
            <div className="sm:col-span-1 min-w-0">
              <label className="text-[11px] font-bold text-slate-600 mb-1 block">Mês</label>
              <MonthFieldPT
                value={filters.month}
                onChange={(v) => setFilters({ ...filters, month: v })}
                className="h-9 text-[13px]"
              />
            </div>
          ) : (
            <>
              <div className="min-w-0">
                <label className="text-[11px] font-bold text-slate-600 mb-1 block">De</label>
                <DateFieldPT
                  value={filters.date_from}
                  onChange={(v) => setFilters({ ...filters, date_from: v })}
                />
              </div>
              <div className="min-w-0">
                <label className="text-[11px] font-bold text-slate-600 mb-1 block">Até</label>
                <DateFieldPT
                  value={filters.date_to}
                  onChange={(v) => setFilters({ ...filters, date_to: v })}
                />
              </div>
            </>
          )}
          <div>
            <label className="text-[11px] font-bold text-slate-600 mb-1 block">Escola</label>
            <Select
              value={filters.school_id || "__any__"}
              onValueChange={(v) => setFilters({ ...filters, school_id: v === "__any__" ? "" : v })}
            >
              <SelectTrigger className="h-9 text-[13px]">
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__any__">Todas as escolas</SelectItem>
                {schools.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] font-bold text-slate-600 mb-1 block">Nº orçamento</label>
            <Input
              type="number"
              placeholder="ex.: 1234"
              value={filters.budget_number}
              onChange={(e) => setFilters({ ...filters, budget_number: e.target.value })}
              className="h-9 text-[13px]"
            />
          </div>
        </div>
        {(filters.school_id || filters.budget_number || filters.date_from || filters.date_to) && (
          <div className="mt-3">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setFilters({ month: filters.month, date_from: "", date_to: "", school_id: "", budget_number: "" })}
              className="h-7 text-[11px] text-slate-600"
            >
              Limpar filtros
            </Button>
          </div>
        )}
      </section>

      {/* By month trend */}
      {byMonth.length > 0 && (
        <section className="bg-white rounded-2xl border border-slate-200 p-4 card-shadow">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            <h2 className="font-heading text-sm font-black text-slate-900">Últimos meses</h2>
          </div>
          <ul className="divide-y divide-slate-100">
            {byMonth.map((m) => (
              <li key={m.month} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-[12.5px] font-bold text-slate-900">{m.month}</span>
                </div>
                <span className="text-[12.5px] font-black tabular-nums text-emerald-700">{BRL(m.total)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Items */}
      <section className="bg-white rounded-2xl border border-slate-200 p-4 card-shadow">
        <div className="flex items-center gap-2 mb-3">
          <Receipt className="w-4 h-4 text-slate-700" />
          <h2 className="font-heading text-sm font-black text-slate-900">
            Orçamentos no período
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadSocioCsv(socio, items, periodLabel.replace(/[^a-z0-9\-]+/gi, "-"))}
            disabled={!items.length}
            data-testid="btn-export-socio-csv"
            className="ml-auto h-7 text-[11px] font-bold"
          >
            <Download className="w-3.5 h-3.5 mr-1" />
            Exportar CSV
          </Button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-6 text-slate-500">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando…
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Sem orçamentos nesse período.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-[10px] uppercase tracking-widest text-slate-500 text-left">
                <tr>
                  <th className="pb-2">NF / data</th>
                  <th className="pb-2">Escola</th>
                  <th className="pb-2 text-right">Bruto</th>
                  <th className="pb-2 text-right">Líquido</th>
                  <th className="pb-2 text-right">% / Faixa</th>
                  <th className="pb-2 text-right">Valor sócio</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr
                    key={r.budget_id}
                    className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`socio-item-${r.budget_id}`}
                  >
                    <td className="py-2">
                      <Link to={`/orcamentos/${r.budget_id}`} className="font-black text-slate-900 hover:text-emerald-700">
                        #{r.budget_number}
                      </Link>
                      <p className="text-[10px] text-slate-500">{formatDateBR(r.nf_date)}</p>
                    </td>
                    <td className="py-2 truncate max-w-[200px]">{r.school_name || "—"}</td>
                    <td className="py-2 text-right tabular-nums">{BRL(r.gross)}</td>
                    <td className="py-2 text-right tabular-nums">{BRL(r.net_after_taxes_expenses)}</td>
                    <td className="py-2 text-right tabular-nums text-slate-500">
                      {r.pct}% / F{r.tier}
                    </td>
                    <td className="py-2 text-right tabular-nums font-black text-emerald-700">
                      {BRL(r.value)}
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-slate-900 bg-slate-50">
                  <td colSpan={5} className="py-2 text-[11px] font-black text-slate-700 uppercase tracking-widest">
                    Total do período
                  </td>
                  <td className="py-2 text-right tabular-nums font-black text-emerald-700 text-base">
                    {BRL(total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
